import React from 'react';
import {createRoot} from 'react-dom/client';
import {App} from '../../app/AppSingleton';
import './DataManagementDialog.css';

interface DataManagementDialogProps {
	isOpen: boolean;
	onClose: () => void;
}

interface DataManagementDialogState {
	isExporting: boolean;
	isImporting: boolean;
	isErasing: boolean;
	importError: string | null;
}

interface ActionButtonSpec {
	id: string;
	label: string;
	labelInProgress: string;
	variant: 'export' | 'import' | 'erase';
	handler: () => void;
	inputType?: 'file';
	accept?: string;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

class DataManagementDialog extends React.Component<
	DataManagementDialogProps,
	DataManagementDialogState
> {
	private app = App.getInstance();
	private classBlock = 'data-management-dialog';

	constructor(props: DataManagementDialogProps) {
		super(props);
		this.state = {
			isExporting: false,
			isImporting: false,
			isErasing: false,
			importError: null,
		};
	}

	private handleExport = async () => {
		this.setState({ isExporting: true });

		// Используем метод экспорта из AppSingleton
		this.app.exportData()
			.then(exportData => {
				// Создаем Blob и ссылку для скачивания
				const blob = new Blob([JSON.stringify(exportData, null, 2)], {
					type: 'application/json',
				});
				const url = URL.createObjectURL(blob);

				const a = document.createElement('a');
				a.href = url;
				a.download = `sitask-export-${new Date().toISOString().slice(0, 19)}.json`;
				document.body.appendChild(a);
				a.click();

				// Убираем временную ссылку
				setTimeout(() => {
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
				}, 100);
			})
			.catch(error => {
				console.error('Ошибка при экспорте данных:', error);
			})
			.finally(() => {
				this.setState({isExporting: false});
			})
	};

	private handleImport = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = event.target.files?.[0];
		if (!file) {
			return;
		}
		if (!window.confirm('Вы уверены, что хотите стереть все данные и заменить новыми? Это действие нельзя отменить.')) {
			return;
		}
		this.setState({ isImporting: true, importError: null });

		try {

			const text = await file.text();
			const importData = JSON.parse(text);

			// Проверяем формат импортируемых данных
			if (!importData.version) {
				throw new Error('Неверный формат файла импорта');
			}

			// Вызываем метод импорта из AppSingleton
			await this.app.importData(importData);

			alert('Данные успешно импортированы!');
			this.props.onClose();
		} catch (error) {
			console.error('Ошибка при импорте данных:', error);
			this.setState({
				importError:
					error instanceof Error
						? error.message
						: 'Неизвестная ошибка при импорте',
			});
		} finally {
			this.setState({ isImporting: false });
			// Сбрасываем значение инпута, чтобы можно было повторно загрузить тот же файл
			event.target.value = '';
		}
	};

	private handleErase = async () => {
		if (!window.confirm('Вы уверены, что хотите стереть все данные? Это действие нельзя отменить.')) {
			return;
		}

		this.setState({ isErasing: true });

		try {
			// Вызываем метод очистки данных из AppSingleton
			await this.app.eraseAllData();

			alert('Все данные были успешно стерты!');
			this.props.onClose();
		} catch (error) {
			console.error('Ошибка при очистке данных:', error);
		} finally {
			this.setState({ isErasing: false });
		}
	};

	private actionButtonsSpec: ActionButtonSpec[] = [
		{
			id: 'export',
			label: 'Экспортировать данные',
			labelInProgress: 'Экспорт...',
			variant: 'export',
			handler: this.handleExport,
		},
		{
			id: 'import',
			label: 'Импортировать данные',
			labelInProgress: 'Импорт...',
			variant: 'import',
			handler: () => {}, // Обработчик будет передан отдельно
			inputType: 'file',
			accept: '.json',
			onChange: this.handleImport,
		},
		{
			id: 'erase',
			label: 'Стереть всё',
			labelInProgress: 'Очистка...',
			variant: 'erase',
			handler: this.handleErase,
		},
	];

	private getActionButtonsSpec = (): ActionButtonSpec[] => {
		return this.actionButtonsSpec;
	};

	private renderActionButtons = (): React.ReactNode => {
		const classElement = this.classBlock;
		const classActionButton = `${classElement}__action-button`;
		const classExportButton = `${classActionButton} ${classElement}__export-button`;
		const classImportButton = `${classActionButton} ${classElement}__import-button`;
		const classEraseButton = `${classActionButton} ${classElement}__erase-button`;

		return this.getActionButtonsSpec().map(spec => {
			const buttonClassMap = {
				export: classExportButton,
				import: classImportButton,
				erase: classEraseButton,
			};

			const buttonClass = buttonClassMap[spec.variant];

			// Получаем состояния по id из this.state
			const stateKey = `is${spec.id.charAt(0).toUpperCase() + spec.id.slice(1)}`;
			const isLoading = this.state[
				stateKey as keyof DataManagementDialogState
			] as boolean;
			const disabled = isLoading;

			// Определяем текст кнопки в зависимости от состояния
			const labelText = isLoading ? spec.labelInProgress : spec.label;

			if (spec.inputType === 'file') {
				// Для кнопки импорта используем label с input
				return (
					<label key={spec.id} className={buttonClass}>
						{labelText}
						<input
							type={spec.inputType}
							accept={spec.accept}
							onChange={spec.onChange}
							disabled={disabled}
						/>
					</label>
				);
			} else {
				// Для остальных кнопок используем обычную кнопку
				return (
					<button
						key={spec.id}
						className={buttonClass}
						onClick={spec.handler}
						disabled={disabled}
					>
						{labelText}
					</button>
				);
			}
		});
	};

	private dialogRef = React.createRef<HTMLDialogElement>();

	componentDidUpdate(prevProps: DataManagementDialogProps) {
		if (prevProps.isOpen !== this.props.isOpen) {
			if (this.props.isOpen) {
				document.body.style.overflow = 'hidden';
				// Открываем диалог как модальное окно
				if (this.dialogRef.current && !this.dialogRef.current.open) {
					this.dialogRef.current.showModal();
				}
			} else {
				document.body.style.overflow = 'unset';
				this.setState({ importError: null });
				// Закрываем диалог
				if (this.dialogRef.current && this.dialogRef.current.open) {
					this.dialogRef.current.close();
				}
			}
		}
	}

	render() {
		const { onClose } = this.props;
		const { importError } = this.state;

		const classElement = this.classBlock;
		const classContent = `${classElement}__content`;
		const classHeader = `${classElement}__header`;
		const classTitle = `${classElement}__title`;
		const classCloseButton = `${classElement}__close-button`;
		const classBody = `${classElement}__body`;
		const classActions = `${classElement}__actions`;
		const classError = `${classElement}__error`;
		const classInfo = `${classElement}__info`;

		return (
			<dialog
				ref={this.dialogRef}
				className={classElement}
				onCancel={onClose} // Обработчик закрытия диалога (например, при нажатии Escape)
			>
				<div className={classContent}>
					<div className={classHeader}>
						<h2 className={classTitle}>Управление данными</h2>
						<button
							className={classCloseButton}
							onClick={onClose}
							aria-label="Закрыть"
						>
							✕
						</button>
					</div>

					<div className={classBody}>
						<div className={classActions}>
							{this.renderActionButtons()}
						</div>

						{importError && (
							<div className={classError}>
								Ошибка импорта: {importError}
							</div>
						)}

						<div className={classInfo}>
							<p>
								<strong>Экспорт:</strong> Сохраняет все ваши
								задачи, колонки и настройки в JSON файл.
							</p>
							<p>
								<strong>Импорт:</strong> Загружает данные из
								ранее экспортированного JSON файла.
							</p>
							<p>
								<strong>Стереть всё:</strong> Удаляет все данные
								приложения. Это действие нельзя отменить!
							</p>
						</div>
					</div>
				</div>
			</dialog>
		);
	}

	// Статический метод для отображения диалога
	public static show(): void {
		// Создаем контейнер для портала
		const container = document.createElement('div');
		container.setAttribute('id', 'data-management-dialog-portal');
		document.body.appendChild(container);

		// Создаем root
		const root = createRoot(container);

		// Функция для закрытия диалога
		const closeDialog = () => {
			root.unmount();
			document.body.removeChild(container);
		};

		// Рендерим компонент
		root.render(
			<DataManagementDialog isOpen={true} onClose={closeDialog} />
		);
		
		// Ждем, пока компонент смонтируется, и вызываем showModal()
		setTimeout(() => {
			const dialogElement = container.querySelector('.data-management-dialog') as HTMLDialogElement;
			if (dialogElement && typeof dialogElement.showModal === 'function') {
				dialogElement.showModal();
			}
		}, 0);
	}
}

export { DataManagementDialog };